<template>
  <div>
    <ComponentHeader
      title="Provider Roster Report"
      subtitle="Providers per TIN, derived from the practice hierarchy. Generate re-walks the hierarchy and adds anyone new as pending — it never changes a provider already under review."
    >
      <ExportMenu
        v-if="report"
        :disabled="!visibleCount"
        :exports="[{ type: 'excel', action: exportExcel, loading: exporting }]"
      />
      <button class="btn-primary-sm" :disabled="loading" @click="generate">
        {{ loading ? 'Generating…' : 'Generate / Refresh' }}
      </button>
    </ComponentHeader>

    <div class="p-5">
    <!-- Error -->
    <div v-if="error" class="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
      {{ error }}
    </div>

    <!-- Last generate summary -->
    <div
      v-if="lastRun"
      class="mb-4 p-4 bg-blue-100 border border-blue-300 rounded-lg text-sm text-blue-800"
    >
      Walked {{ lastRun.candidates }} provider–TIN pairs across {{ lastRun.tins }} TINs ·
      <strong>{{ lastRun.created }} new</strong> saved as pending
      <span v-if="lastRun.already_on_file">
        · {{ lastRun.already_on_file }} already on file, left untouched
      </span>
      <span v-if="lastRun.providers_without_npi">
        · {{ lastRun.providers_without_npi }} have no NPI on file
      </span>
    </div>

    <!-- Empty state -->
    <div
      v-if="!report && !initialLoading && !loading && !error"
      class="py-12 text-center text-sm text-gray-500"
    >
      No providers stored for this tenant yet. Click "Generate / Refresh" to derive
      them from the practice hierarchy.
    </div>

    <!-- Loading -->
    <LoadingIndicator
      v-if="loading && !report"
      message="Deriving providers from the practice hierarchy…"
    />

    <template v-if="report">
      <!-- Summary — the whole roster at a glance, above the practice-by-practice
           detail. Stat tiles are the accountability frame (they always count the
           full roster, not the filtered view); clicking one drills the table
           below to that status. -->
      <div class="mb-5">
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          <button
            type="button"
            class="text-left rounded-lg border px-4 py-3 transition-colors"
            :class="statusFilter === null ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'"
            @click="filterStatus(null)"
          >
            <div class="text-2xl font-semibold text-gray-900 tabular-nums">{{ summary.total.toLocaleString() }}</div>
            <div class="text-xs font-medium text-gray-700">Providers on roster</div>
            <div class="text-xs text-gray-400">{{ summary.practicesTotal.toLocaleString() }} practices · {{ summary.tins.toLocaleString() }} TINs</div>
          </button>

          <button
            type="button"
            class="text-left rounded-lg border px-4 py-3 transition-colors"
            :class="statusFilter === 'approved' ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'"
            @click="filterStatus('approved')"
          >
            <div class="text-2xl font-semibold text-emerald-700 tabular-nums">{{ summary.approved.toLocaleString() }}</div>
            <div class="text-xs font-medium text-gray-700">Confirmed</div>
            <div class="text-xs text-gray-400">{{ pct(summary.approved) }} of roster</div>
          </button>

          <button
            type="button"
            class="text-left rounded-lg border px-4 py-3 transition-colors"
            :class="statusFilter === 'pending' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-300'"
            @click="filterStatus('pending')"
          >
            <div class="text-2xl font-semibold text-amber-700 tabular-nums">{{ summary.pending.toLocaleString() }}</div>
            <div class="text-xs font-medium text-gray-700">Outstanding</div>
            <div class="text-xs text-gray-400">{{ pct(summary.pending) }} awaiting review</div>
          </button>

          <button
            type="button"
            class="text-left rounded-lg border px-4 py-3 transition-colors"
            :class="statusFilter === 'rejected' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-300'"
            @click="filterStatus('rejected')"
          >
            <div class="text-2xl font-semibold text-red-700 tabular-nums">{{ summary.rejected.toLocaleString() }}</div>
            <div class="text-xs font-medium text-gray-700">To remove</div>
            <div class="text-xs text-gray-400">rejected by review</div>
          </button>

          <div class="rounded-lg border border-gray-200 px-4 py-3">
            <div class="text-2xl font-semibold text-blue-700 tabular-nums">{{ summary.added.toLocaleString() }}</div>
            <div class="text-xs font-medium text-gray-700">Added by practice</div>
            <div class="text-xs text-gray-400">not on the CMS list</div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <!-- Review progress — status composition of the whole roster as one bar. -->
          <div class="rounded-lg border border-gray-200 p-4">
            <div class="flex items-baseline justify-between mb-2">
              <span class="text-xs font-semibold text-gray-700 uppercase tracking-wider">Review progress</span>
              <span class="text-xs text-gray-500">
                {{ pct(summary.reviewed) }} reviewed · {{ summary.attested.toLocaleString() }} of {{ summary.practicesTotal.toLocaleString() }} practices done
              </span>
            </div>
            <div class="flex h-3 w-full gap-0.5 rounded-full overflow-hidden bg-gray-100">
              <div
                v-for="seg in statusSegments"
                :key="seg.key"
                :class="seg.bar"
                :style="{ width: seg.pct + '%' }"
                :title="`${seg.label}: ${seg.count.toLocaleString()} (${pct(seg.count)})`"
              />
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              <span v-for="seg in statusSegments" :key="seg.key" class="flex items-center gap-1.5 text-xs text-gray-600">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" :class="seg.dot" />
                {{ seg.label }} <span class="tabular-nums font-medium text-gray-800">{{ seg.count.toLocaleString() }}</span>
              </span>
              <span v-if="summary.noNpi" class="flex items-center gap-1.5 text-xs text-gray-500">
                · {{ summary.noNpi.toLocaleString() }} missing NPI
              </span>
            </div>
          </div>

          <!-- Outstanding by practice — Jon's "who are the 800 from?". Click a bar
               to drill the table into that practice. -->
          <div class="rounded-lg border border-gray-200 p-4">
            <div class="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
              Top practices outstanding
            </div>
            <div v-if="summary.topOutstanding.length" class="space-y-1.5">
              <button
                v-for="p in summary.topOutstanding"
                :key="p.key"
                type="button"
                class="group flex items-center gap-3 w-full text-left"
                :title="`${p.name} — ${p.pending} outstanding`"
                @click="focusPractice(p.name)"
              >
                <span class="sensitive truncate text-xs text-gray-700 group-hover:text-gray-900 w-36 shrink-0">{{ p.name || '(unnamed)' }}</span>
                <span class="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <span class="block h-full rounded-full bg-amber-400 group-hover:bg-amber-500 transition-colors" :style="{ width: barPct(p.pending) + '%' }" />
                </span>
                <span class="tabular-nums text-xs text-gray-600 w-10 text-right shrink-0">{{ p.pending.toLocaleString() }}</span>
              </button>
            </div>
            <div v-else class="text-sm text-gray-400 py-4 text-center">
              Nothing outstanding — every provider has been reviewed.
            </div>
          </div>
        </div>
      </div>

      <!-- Controls -->
      <div class="flex items-end gap-4 mb-4 flex-wrap">
        <div class="grow max-w-xs">
          <label class="block text-xs font-medium text-gray-700 mb-1">Search</label>
          <input
            v-model="search" data-demo-search
            type="text"
            placeholder="TIN, practice, provider, NPI…"
            class="sensitive w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          />
        </div>
        <FilterDropdown
          v-model="statusFilter"
          label="Status"
          :options="statusOptions"
          :sensitive-labels="false"
          placeholder="All statuses"
          clearable
        />
        <button
          v-if="search || statusFilter"
          class="text-xs text-blue-600 hover:underline pb-2"
          @click="resetFilters"
        >
          Reset
        </button>
        <button
          v-if="filteredGroups.length"
          class="text-xs text-blue-600 hover:underline pb-2"
          @click="allCollapsed ? expandAll() : collapseAll()"
        >
          {{ allCollapsed ? 'Expand all' : 'Collapse all' }}
        </button>
        <div class="ml-auto flex items-end gap-4 pb-1">
          <span class="text-xs text-gray-600">
            {{ visibleCount.toLocaleString() }} providers · {{ filteredGroups.length.toLocaleString() }} TINs
          </span>
        </div>
      </div>

      <!-- Table -->
      <div class="border border-gray-200 rounded-lg overflow-auto" style="max-height: calc(100vh - 340px)">
        <table class="w-full text-sm">
          <thead class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700 transition-colors"
                @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <span v-if="sortKey === col.key" class="ml-0.5 text-primary-500">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in filteredGroups" :key="group.tin">
              <!-- TIN group header — click to fold/unfold. Providers pile up under
                   a single TIN, so a big practice collapses to one line. -->
              <tr
                class="border-t border-gray-200 bg-gray-50/60 hover:bg-blue-50/40 cursor-pointer"
                @click="toggleGroup(group.tin)"
              >
                <td class="px-4 py-3 font-mono text-xs text-gray-800 whitespace-nowrap align-top">
                  <span class="inline-block w-4 text-gray-500">{{ isCollapsed(group.tin) ? '▸' : '▾' }}</span> <span class="sensitive">{{ group.tin }}</span>
                </td>
                <td class="px-4 py-3 align-top">
                  <router-link
                    v-if="practiceLink(group)"
                    :to="practiceLink(group)"
                    class="text-gray-900 font-medium hover:text-neutral-700 hover:underline sensitive"
                    @click.stop
                  >
                    {{ group.practice_name }}
                  </router-link>
                  <span v-else class="text-gray-900 font-medium sensitive">{{ group.practice_name }}</span>
                  <div class="flex items-center gap-2 mt-1 flex-wrap">
                    <Tag :label="`${group.count} provider${group.count === 1 ? '' : 's'}`" color="blue" />
                    <button
                      v-if="group.practice_entity"
                      class="text-xs text-gray-600 hover:text-gray-800 hover:underline"
                      @click.stop="$emit('preview-practice', group.practice_entity)"
                    >
                      Practice view →
                    </button>
                  </div>
                </td>
                <td class="px-3 py-3 align-top" />
                <td class="px-3 py-3 align-top" />
                <td class="px-3 py-3 align-top">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <Tag v-if="group.statusCounts.pending" :label="`${group.statusCounts.pending} pending`" color="amber" />
                    <Tag v-if="group.statusCounts.approved" :label="`${group.statusCounts.approved} confirmed`" color="emerald" />
                    <Tag v-if="group.statusCounts.rejected" :label="`${group.statusCounts.rejected} rejected`" color="red" />
                  </div>
                </td>
              </tr>

              <!-- Provider rows, hidden when the group is folded -->
              <tr
                v-for="row in (isCollapsed(group.tin) ? [] : group.providers)"
                :key="row.id"
                class="hover:bg-blue-50/30"
              >
                <td class="px-4 py-3 align-top" />
                <td class="px-4 py-3 align-top" />
                <td class="px-3 py-3 font-mono text-xs text-gray-800 align-top whitespace-nowrap sensitive">
                  {{ row.npi || '—' }}
                </td>
                <td class="px-3 py-3 text-gray-800 align-top sensitive">
                  {{ row.provider_name || '(unnamed)' }}
                </td>
                <td class="px-3 py-3 align-top">
                  <Tag :label="statusLabel(row.status)" :color="statusColor(row.status)" />
                  <div v-if="row.source === 'practice_added'" class="mt-1">
                    <Tag label="Added by practice" color="blue" />
                  </div>
                  <div v-if="row.practice_reviewer_name" class="text-xs text-gray-600 mt-1">
                    {{ row.practice_reviewer_name }}
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="filteredGroups.length === 0">
              <td :colspan="columns.length" class="px-4 py-12 text-center text-sm text-gray-500">
                No providers match your filters. Clear the search or status filter to
                see all {{ (report || []).length.toLocaleString() }} providers.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { canAccessEntity } from '@/utils/access'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportToExcel } from '@/utils/exportArraytoXlsx'
import demoProtectedFields from '@/mixins/demoProtectedFields'

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Confirmed',
  rejected: 'Rejected',
}
const STATUS_COLORS = {
  pending: 'amber',
  approved: 'emerald',
  rejected: 'red',
}
const SOURCE_LABELS = {
  derived: 'Derived',
  practice_added: 'Added by practice',
  staff_added: 'Added by staff',
}

export default {
  name: 'ProviderRosterReport',
  components: { ComponentHeader, LoadingIndicator, FilterDropdown, Tag, ExportMenu },
  mixins: [demoProtectedFields],
  // The TIN/practice/provider/NPI search identifies real providers and practices. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  emits: ['preview-practice'],
  data() {
    return {
      loading: false,
      exporting: false,
      error: null,
      report: null,
      initialLoading: true,
      lastRun: null,
      search: '',
      statusFilter: null,
      sortKey: 'practice_name',
      sortDir: 'asc',
      // Explicit per-TIN fold state the user has set (true = open, false = closed).
      // A TIN absent from the map takes the search-aware default in isCollapsed:
      // closed while browsing (summary-first), open while searching so matches show.
      openTins: {},
      columns: [
        { key: 'tin', label: 'TIN' },
        { key: 'practice_name', label: 'Practice' },
        { key: 'npi', label: 'NPI' },
        { key: 'provider_name', label: 'Provider' },
        { key: 'status', label: 'Status' },
      ],
      statusOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Confirmed' },
        { value: 'rejected', label: 'Rejected' },
      ],
    }
  },
  computed: {
    filteredGroups() {
      if (!this.report) return []
      let rows = this.report
      if (this.statusFilter) rows = rows.filter(r => r.status === this.statusFilter)
      const q = this.search.trim().toLowerCase()
      if (q) {
        rows = rows.filter(r =>
          String(r.tin).includes(q) ||
          (r.practice_name || '').toLowerCase().includes(q) ||
          (r.provider_name || '').toLowerCase().includes(q) ||
          String(r.npi || '').includes(q)
        )
      }

      // Group by TIN, then sort GROUPS by the chosen column (a group sorts by its
      // smallest value for that column, same as the address report).
      const dir = this.sortDir === 'asc' ? 1 : -1
      const k = this.sortKey

      const groups = []
      const byTin = new Map()
      for (const r of rows) {
        let g = byTin.get(r.tin)
        if (!g) {
          g = { tin: r.tin, practice_name: r.practice_name, practice_entity: r.practice_entity, rows: [] }
          byTin.set(r.tin, g)
          groups.push(g)
        }
        g.rows.push(r)
      }

      const numeric = rows.some(r => typeof r[k] === 'number')
      const keyOf = (g) => {
        const vals = g.rows.map(r => r[k])
        return numeric
          ? Math.min(...vals.map(v => (typeof v === 'number' ? v : Number.NEGATIVE_INFINITY)))
          : vals.map(v => String(v ?? '')).sort()[0]
      }
      const cmp = numeric
        ? (a, b) => dir * (keyOf(a) - keyOf(b))
        : (a, b) => dir * String(keyOf(a)).localeCompare(String(keyOf(b)))

      groups.sort((a, b) => cmp(a, b) || String(a.tin ?? '').localeCompare(String(b.tin ?? '')))

      return groups.map(g => {
        const providers = [...g.rows].sort((a, b) =>
          String(a.provider_name ?? '').localeCompare(String(b.provider_name ?? ''))
        )
        const statusCounts = { pending: 0, approved: 0, rejected: 0 }
        for (const r of providers) {
          if (statusCounts[r.status] !== undefined) statusCounts[r.status]++
        }
        return {
          tin: g.tin,
          practice_name: g.practice_name,
          practice_entity: g.practice_entity,
          providers,
          count: providers.length,
          statusCounts,
        }
      })
    },
    visibleCount() {
      return this.filteredGroups.reduce((n, g) => n + g.count, 0)
    },
    allCollapsed() {
      const groups = this.filteredGroups
      return groups.length > 0 && groups.every(g => this.isCollapsed(g.tin))
    },

    // The whole roster at a glance — always the full set, never the filtered view,
    // so the accountability totals stay put while the table below is filtered.
    summary() {
      const rows = this.report || []
      const s = { total: rows.length, pending: 0, approved: 0, rejected: 0, added: 0, noNpi: 0 }
      const tins = new Set()
      // Keyed by practice entity where we have one, else the name, else the TIN, so
      // a null-entity practice is not collapsed into one bucket with every other.
      const practices = new Map()
      for (const r of rows) {
        if (s[r.status] !== undefined) s[r.status]++
        if (r.source && r.source !== 'derived') s.added++
        if (!r.npi) s.noNpi++
        tins.add(r.tin)
        const key = r.practice_entity != null
          ? `e:${r.practice_entity}`
          : `n:${r.practice_name || ''}|t:${r.tin}`
        let p = practices.get(key)
        if (!p) {
          p = { key, name: r.practice_name, total: 0, pending: 0 }
          practices.set(key, p)
        }
        p.total++
        if (r.status === 'pending') p.pending++
      }
      let attested = 0
      for (const p of practices.values()) if (p.pending === 0) attested++
      const topOutstanding = [...practices.values()]
        .filter(p => p.pending > 0)
        .sort((a, b) => b.pending - a.pending || String(a.name ?? '').localeCompare(String(b.name ?? '')))
        .slice(0, 8)
      return {
        ...s,
        tins: tins.size,
        practicesTotal: practices.size,
        attested,
        reviewed: s.approved + s.rejected,
        topOutstanding,
      }
    },

    // Status composition of the whole roster, as the segmented review-progress bar.
    // Zero-count segments drop out so they take no width and no legend row.
    statusSegments() {
      const total = this.summary.total || 1
      return [
        { key: 'approved', label: 'Confirmed', count: this.summary.approved, bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
        { key: 'pending', label: 'Outstanding', count: this.summary.pending, bar: 'bg-amber-400', dot: 'bg-amber-400' },
        { key: 'rejected', label: 'To remove', count: this.summary.rejected, bar: 'bg-red-500', dot: 'bg-red-500' },
      ]
        .filter(seg => seg.count > 0)
        .map(seg => ({ ...seg, pct: (seg.count / total) * 100 }))
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    // Canonical entity route, gated by the same access check the AG-Grid
    // entity-link cell uses. Null when the user cannot open that entity.
    practiceLink(group) {
      if (!group.practice_entity || !canAccessEntity(group.practice_entity)) return null
      return `/entity/${group.practice_entity}`
    },
    isCollapsed(tin) {
      const explicit = this.openTins[tin]
      if (explicit !== undefined) return !explicit
      // Default: closed while browsing, open while searching so matches are visible.
      return !this.search.trim()
    },
    toggleGroup(tin) {
      this.openTins = { ...this.openTins, [tin]: this.isCollapsed(tin) }
    },
    collapseAll() {
      const next = {}
      for (const g of this.filteredGroups) next[g.tin] = false
      this.openTins = next
    },
    expandAll() {
      const next = {}
      for (const g of this.filteredGroups) next[g.tin] = true
      this.openTins = next
    },
    // Summary helpers.
    pct(n) {
      const total = this.summary.total
      if (!total) return '0%'
      return `${Math.round((n / total) * 100)}%`
    },
    barPct(pending) {
      const max = this.summary.topOutstanding[0]?.pending || 1
      return Math.max(4, (pending / max) * 100)
    },
    filterStatus(status) {
      this.statusFilter = status
    },
    focusPractice(name) {
      this.search = name || ''
      this.statusFilter = null
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    statusColor(status) {
      return STATUS_COLORS[status] || 'gray'
    },
    resetFilters() {
      this.search = ''
      this.statusFilter = null
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
    },
    async load() {
      try {
        const { data } = await axios.get('/api/rostering/providers/')
        this.report = data.results || data
      } catch (e) {
        this.error = e.response?.data?.detail || e.response?.data?.error || 'Failed to load providers'
      } finally {
        this.initialLoading = false
      }
    },

    // Re-walk the hierarchy and persist anyone new as pending. Postgres, so fast.
    async generate() {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.post('/api/rostering/providers/generate/')
        this.lastRun = data
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.detail || e.response?.data?.error || 'Failed to generate'
      } finally {
        this.loading = false
      }
    },
    // Our standard xlsx export, one row per provider in the current filtered view —
    // for ad hoc inquiries and mail merges. Carries the review columns the table
    // hides (source, reviewer, comment). Demo Mode is handled inside exportToExcel.
    async exportExcel() {
      this.exporting = true
      try {
        const rows = []
        for (const g of this.filteredGroups) {
          for (const r of g.providers) {
            rows.push({
              TIN: r.tin,
              Practice: r.practice_name || '',
              NPI: r.npi || '',
              Provider: r.provider_name || '',
              Status: STATUS_LABELS[r.status] || r.status,
              Source: SOURCE_LABELS[r.source] || r.source || '',
              'Reviewed by': r.practice_reviewer_name || '',
              Comment: r.practice_comment || '',
            })
          }
        }
        await exportToExcel(
          rows,
          `provider-roster-${new Date().toISOString().slice(0, 10)}.xlsx`,
          {
            sheetName: 'Provider Roster',
            columnWidths: {
              TIN: 90, Practice: 220, NPI: 110, Provider: 200,
              Status: 90, Source: 130, 'Reviewed by': 150, Comment: 260,
            },
          },
        )
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>
